import type { PostalCodeChangeEvent } from "../Inputs/LocationsPostalCodeInput";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { locationsErrorMessagesForLocation } from "../Validators/locationsErrorMessagesForLocation";
import type { Accessorials } from "@/Shared/types";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { useState } from "react";
import { warningMessagesForDeliveryLocation } from "../Validators/locationsErrorMessagesForDeliveryLocation";
import {
  generateRecommendedAccessorialsForLocation,
  removeUnavailableAccessorialsForLocation,
} from "@/Shared/Locations/Helpers/recommendedAccessorialsForLocation";
import LocationsDetails from "../CustomComponents/LocationsDetails";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";

interface LocationsAddLocationDialogDetailsPage {
  setLocation: (_: AddLocationLocationType) => void;
  location: AddLocationLocationType;
  onSwitchToAddressPage: () => void;
  onCancel: () => void;
  context: LocationContext;
}

function LocationsAddLocationDialogDetailsPage(
  props: LocationsAddLocationDialogDetailsPage,
) {
  const { setLocation, location, onSwitchToAddressPage, onCancel } = props;

  // TODO: turn false into something meaningful
  const locationErrorMessages = locationsErrorMessagesForLocation(
    location,
    false,
  );
  // We're using the delivery location here because delivery includes
  // appointments (whereas pickup currently does not)
  const locationWarningMessages = warningMessagesForDeliveryLocation(
    undefined,
    location,
  );
  function isValid() {
    if (locationErrorMessages.address.postalCode !== undefined) {
      return false;
    }

    if (locationErrorMessages.distributionWarehouseBrand !== undefined) {
      return false;
    }

    if (locationErrorMessages.locationType !== undefined) {
      return false;
    }

    return true;
  }

  const address = location.address;

  const [forceValidation, setForceValidation] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [distributionWarehouseName, setDistributionWarehouseName] = useState<
    string | undefined
  >();

  const accessorials = location.accessorials;

  function onPostalCodeChange(
    e: PostalCodeChangeEvent,
    _distributionWarehouseName: string | undefined,
  ) {
    if (e.latitudeLongitude !== undefined) {
      setShowMap(true);
    } else {
      setShowMap(false);
    }

    const newLocation = {
      ...location,
      ...{ address: e.address, latitudeLongitude: e.latitudeLongitude },
    };

    setLocation(newLocation);
    setDistributionWarehouseName(_distributionWarehouseName);
  }

  function setLocationType(locationType: LocationType) {
    const newLocation = {
      ...location,
      ...{ locationType },
      // Recommend what the new type needs, then drop what it can't have. The
      // second half matters on its own: switching to Residential with inside
      // ticked would otherwise leave that row checked and disabled, which the
      // customer has no way to undo.
      accessorials: removeUnavailableAccessorialsForLocation(
        locationType,
        generateRecommendedAccessorialsForLocation(
          locationType,
          location.accessorials,
        ),
      ),
    };

    setLocation(newLocation);
  }

  function setDistributionWarehouseBrand(
    distributionWarehouseBrand: string | undefined,
  ) {
    const newLocation = {
      ...location,
      ...{ distributionWarehouseBrand },
    };

    setLocation(newLocation);
  }

  function removeItemsStartingWith(values: string[], prefix: string) {
    return values.filter((v) => !v.startsWith(prefix));
  }

  function setSchedulingAccessorials(schedulingAccessorials: Accessorials) {
    const updatedAccessorials = removeItemsStartingWith(
      accessorials,
      "SCHEDULING_",
    );
    updatedAccessorials.push(...schedulingAccessorials);

    setLocation({
      ...location,
      ...{ accessorials: updatedAccessorials },
    });
  }

  function setLogisticsAccessorials(logisticsAccessorials: Accessorials) {
    const updatedAccessorials = removeItemsStartingWith(
      accessorials,
      "LOGISTICS_",
    );
    updatedAccessorials.push(...logisticsAccessorials);
    setLocation({
      ...location,
      ...{ accessorials: updatedAccessorials },
    });
  }

  function onNext() {
    if (isValid()) {
      onSwitchToAddressPage();
    } else {
      setForceValidation(true);
    }
  }

  return (
    // 48px between the details and the nav buttons is past Stack's gap cap, so
    // the wide-gutter primitive carries it.
    <Grid columns={1} gapY="lg">
      <LocationsDetails
        locationErrorMessages={locationErrorMessages}
        locationWarningMessages={locationWarningMessages}
        forceValidation={forceValidation}
        address={address}
        location={location}
        onPostalCodeChange={onPostalCodeChange}
        showMap={showMap}
        locationContext={props.context}
        latitudeLongitude={location.latitudeLongitude}
        setLocationType={setLocationType}
        accessorials={accessorials}
        setSchedulingAccessorials={setSchedulingAccessorials}
        setLogisticsAccessorials={setLogisticsAccessorials}
        disablePostalCodeEntry={false}
        distributionWarehouseName={distributionWarehouseName}
        setDistributionWarehouseBrand={setDistributionWarehouseBrand}
      />
      <Row justify="between">
        <Button onClick={onCancel}>Cancel</Button>
        <Button primary onClick={onNext}>
          Next
        </Button>
      </Row>
    </Grid>
  );
}

export default LocationsAddLocationDialogDetailsPage;
